import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  IgButton,
  IgDialog,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { ExperienceCategory, TabEnumFe } from "@intelligems/ig-types";
import { useBooleanWebUiScopeMap } from "@src/components/LimitAccess/check-scopes-hooks";
import { officialCategoryName } from "@intelligems/ig-utils";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useBillingGateModel } from "@src/components/BillingSettings2026/workflow";
import {
  areOneOfTheseSubsActive,
  useActiveSubscription,
} from "@src/components/LimitAccess/check-subscriptions-hooks";
import {
  useHasRequiredShopifyScopes,
  useHasStorefrontToken,
} from "@src/components/LimitAccess/hooks";
import {
  getFirstCardDisabledReason as getFirstCardDisabledReasonBase,
  getOutOfScopeTier as getOutOfScopeTierBase,
  getRunModeDisabledReason as getRunModeDisabledReasonBase,
  isOnsiteUpsellExperience,
} from "@src/components/Dialogs/CreateExperienceDialog/gating";
import { MissingShopifyScopeDialog } from "@src/components/Dialogs/MissingShopifyScopeDialog";
import { RequestFeatureAccessPylonDialog } from "@src/components/Dialogs/RequestFeatureAccessPylonDialog";
import { useIsUnderOfferLimit } from "@src/features/sharedExperience/hooks/use-is-under-offer-limit";
import { useDialog, useDialogs } from "@src/hooks/dialog";
import { PERSONALIZATION_ROUTES, SETTINGS_ROUTES } from "@src/routes/routes";
import { SettingsSectionId } from "@src/types/navigation";
import {
  bundleOnsiteUpsellRequestForm,
  onsiteUpsellBetaRequestForm,
  postPurchaseFunnelRequestForm,
} from "@src/utils/linkSheet";
import {
  mpExperienceCreated,
  mpGatedModuleAddStarted,
  mpOfferCreationStarted,
  mpOnsiteUpsellCreationStarted,
  mpPostPurchaseFunnelsRequestAccessClick,
  mpPostPurchaseRequestAccessClick,
  mpSmartPricingUpgradeStarted,
} from "@src/utils/mixpanel";
import { PricingGatedModal } from "@src/components/PricingGatedModal";
import {
  CategoryFilter,
  checkoutTest,
  CreateExperimentDialogRenderLocation,
  ExperienceCreationCard,
  OfferPreset,
  offerTest,
  onsiteUpsellTest,
  postPurchaseTest,
  pricingTest,
  shippingTest,
} from "@src/components/Dialogs/CreateExperienceDialog/types";
import { getOfferTypeKey } from "@src/features/sharedExperience/components/offers/utils";
import {
  SMART_PRICING_LIMITED_ID,
  SMART_PRICING_UNLIMITED_ID,
} from "@src/constants/billing";
import { CategorySelectionPage } from "@src/components/Dialogs/CreateExperienceDialog/CategorySelectionPage";
import { TypeSelectionPage } from "@src/components/Dialogs/CreateExperienceDialog/TypeSelectionPage";
import {
  getAvailableCategory,
  getDialogSource,
  getGeneratedTestName,
  getInitialActiveCategory,
  getTestLink,
  getVisibleCards,
} from "@src/components/Dialogs/CreateExperienceDialog/helpers";

export const experienceCreationCards: ExperienceCreationCard[] = [
  {
    id: "content-edits",
    title: "Content Edits",
    description: "Change headlines, copy, and on-page elements.",
    category: "content",
    graphic: "contentEdits",
    filterKeys: ["onsite_test"],
    test: {
      key: "onsite_test",
      experienceType: "content/onsiteEdits",
      mpProductCategory: "content_test",
    },
    personalization: { kind: "mod", modToActivate: "onsiteEdits" },
    isAiPowered: true,
  },
  {
    id: "redirects",
    title: "Split URL",
    description: "Send traffic to a different URL by audience.",
    category: "content",
    graphic: "redirects",
    filterKeys: ["split_test"],
    test: {
      key: "split_test",
      experienceType: "content/url",
      mpProductCategory: "content_test",
    },
    personalization: { kind: "mod", modToActivate: "splitUrl" },
  },
  {
    id: "styles-javascript",
    title: "Styles & Javascript",
    description: "Inject custom CSS and JS safely.",
    category: "content",
    graphic: "stylesAndJavaScript",
    filterKeys: ["onsite_test"],
    test: {
      key: "onsite_test",
      experienceType: "content/onsiteEdits", // TODO - CP this is currently mapped to content edit test
      mpProductCategory: "content_test",
    },
    personalization: { kind: "mod", modToActivate: "onsiteInjections" },
    isAiPowered: true,
  },
  {
    id: "theme-change",
    title: "Theme Change",
    description: "Swap the live theme for a variant.",
    category: "content",
    graphic: "theme",
    filterKeys: ["theme_test"],
    test: {
      key: "theme_test",
      experienceType: "content/theme",
      mpProductCategory: "content_test",
    },
    personalization: { kind: "mod", modToActivate: "theme" },
  },
  {
    id: "template-changes",
    title: "Template Changes",
    description: "Test alternate page templates.",
    category: "content",
    graphic: "template",
    filterKeys: ["template_test"],
    test: {
      key: "template_test",
      experienceType: "content/template",
      mpProductCategory: "content_test",
    },
    personalization: { kind: "mod", modToActivate: "template" },
  },
  {
    id: "merchandising-amount-off-products",
    title: "Amount off Products",
    description: "Discount specific products by percentage or amount.",
    category: "merchandising",
    graphic: "productDiscount",
    filterKeys: ["volume_discount_experiment"],
    test: offerTest({
      discountType: "cartDiscount",
      discountMethod: "perItem",
    }),
    personalization: {
      kind: "offer",
      preset: { discountType: "cartDiscount", discountMethod: "perItem" },
    },
  },
  {
    id: "amount-off-order",
    title: "Amount off Order",
    description: "Apply an automatic cart-wide discount.",
    category: "merchandising",
    graphic: "orderDiscount",
    filterKeys: ["volume_discount_experiment"],
    test: offerTest({
      discountType: "cartDiscount",
      discountMethod: "perOrder",
    }),
    personalization: {
      kind: "offer",
      preset: { discountType: "cartDiscount", discountMethod: "perOrder" },
    },
  },
  {
    id: "volume-discount",
    title: "Volume Discount",
    description: "Reward larger quantities with tiered pricing.",
    category: "merchandising",
    graphic: "volumeDiscount",
    filterKeys: ["volume_discount_experiment"],
    test: offerTest({ discountType: "tieredDiscount" }),
    personalization: {
      kind: "offer",
      preset: { discountType: "tieredDiscount", unitType: "unit" },
    },
  },
  {
    id: "free-gift",
    title: "Free Gift",
    description: "Add a free gift when the cart hits a threshold.",
    category: "merchandising",
    graphic: "freeGift",
    filterKeys: ["volume_discount_experiment"],
    test: offerTest({ discountType: "freeGift" }),
    personalization: { kind: "offer", preset: { discountType: "freeGift" } },
  },
  {
    id: "buy-x-get-y",
    title: "Buy X Get Y",
    description: "Buy X, get Y free or discounted.",
    category: "merchandising",
    graphic: "buyXGetY",
    filterKeys: ["volume_discount_experiment"],
    test: offerTest({ discountType: "buyXGetY", unitType: "unit" }),
    personalization: {
      kind: "offer",
      preset: { discountType: "buyXGetY", unitType: "unit" },
    },
  },
  {
    id: "onsite-upsell",
    title: "Onsite Upsell",
    description: "Recommend add-ons on the product page or in the cart.",
    category: "merchandising",
    graphic: "onsiteUpsell",
    filterKeys: ["onsite_upsell_experiment"],
    isAiPowered: true,
    badgeContent: "New!",
    test: onsiteUpsellTest,
    personalization: { kind: "mod", modToActivate: "onsiteUpsell" },
  },
  {
    id: "bundle-onsite-upsell",
    title: "Bundle Onsite Upsell",
    description:
      "Sell products together at a bundle price, on the product page or in the cart.",
    category: "merchandising",
    graphic: "onsiteBundleUpsell",
    filterKeys: ["onsite_upsell_experiment"],
    isAiPowered: true,
    badgeContent: "Coming soon!",
  },
  {
    id: "price-change",
    title: "Price Change",
    description: "Run true price tests on selected products.",
    category: "pricing",
    graphic: "priceChange",
    filterKeys: ["pricing_experiment"],
    test: pricingTest,
    personalization: { kind: "mod", modToActivate: "price" },
  },
  {
    id: "pricing-amount-off-products",
    title: "Amount off Products",
    description: "Discount specific products by percentage or amount.",
    category: "pricing",
    graphic: "productDiscount",
    filterKeys: ["pricing_experiment"],
    test: pricingTest,
    personalization: {
      kind: "offer",
      preset: { discountType: "cartDiscount", discountMethod: "perItem" },
    },
  },
  {
    id: "shipping-rates",
    title: "Shipping Rates",
    description: "Test rates and free-shipping thresholds by audience.",
    category: "shipping",
    graphic: "shippingRates",
    filterKeys: ["shipping_experiment"],
    test: shippingTest,
  },
  {
    id: "free-shipping",
    title: "Free Shipping",
    description: "Unlock free shipping above a cart value.",
    category: "shipping",
    graphic: "freeShipping",
    filterKeys: ["shipping_experiment"],
    test: offerTest({ discountType: "freeShipping" }),
    personalization: {
      kind: "offer",
      preset: { discountType: "freeShipping" },
    },
  },
  {
    id: "upsell",
    title: "Upsell",
    description: "Recommend add-ons during the checkout flow.",
    category: "checkout",
    graphic: "upsell",
    filterKeys: ["checkout_block_experiment"],
    test: checkoutTest({ checkoutBlockType: "upsell" }),
    personalization: { kind: "checkoutBlock", checkoutBlockType: "upsell" },
  },
  {
    id: "trust-badge",
    title: "Trust Badge",
    description: "Show trust signals to reduce hesitation.",
    category: "checkout",
    graphic: "trustBadge",
    filterKeys: ["checkout_block_experiment"],
    test: checkoutTest({ checkoutBlockType: "trustBadge" }),
    personalization: {
      kind: "checkoutBlock",
      checkoutBlockType: "trustBadge",
    },
  },
  {
    id: "shipping-progress-bar",
    title: "Shipping progress bar",
    description: "Motivate buyers to add more to unlock a reward",
    category: "checkout",
    graphic: "shippingProgress",
    filterKeys: ["checkout_block_experiment"],
    test: checkoutTest({
      checkoutBlockType: "shippingProgressBar",
    }),
    personalization: {
      kind: "checkoutBlock",
      checkoutBlockType: "shippingProgressBar",
    },
  },
  {
    id: "call-out",
    title: "Call Out",
    description: "Highlight a promo or message banner.",
    category: "checkout",
    graphic: "callout",
    filterKeys: ["checkout_block_experiment"],
    test: checkoutTest({
      checkoutBlockType: "trustBadge",
      checkoutBlockTemplate: "callOut",
    }),
    personalization: {
      kind: "checkoutBlock",
      checkoutBlockType: "trustBadge",
      checkoutBlockTemplate: "callOut",
    },
  },
  {
    id: "static-image",
    title: "Static Image",
    description: "Place a custom image block at checkout.",
    category: "checkout",
    graphic: "staticImage",
    filterKeys: ["checkout_block_experiment"],
    test: checkoutTest({
      checkoutBlockType: "trustBadge",
      checkoutBlockTemplate: "images",
    }),
    personalization: {
      kind: "checkoutBlock",
      checkoutBlockType: "trustBadge",
      checkoutBlockTemplate: "images",
    },
  },
  {
    id: "brand-logo",
    title: "Brand Logo",
    description: "Display your logo for added assurance.",
    category: "checkout",
    graphic: "brandLogo",
    filterKeys: ["checkout_block_experiment"],
    test: checkoutTest({
      checkoutBlockType: "trustBadge",
      checkoutBlockTemplate: "brandLogo",
    }),
    personalization: {
      kind: "checkoutBlock",
      checkoutBlockType: "trustBadge",
      checkoutBlockTemplate: "brandLogo",
    },
  },
  {
    id: "countdown-timer",
    title: "Countdown Timer",
    description: "Add urgency with a live countdown.",
    category: "checkout",
    graphic: "countdownTimer",
    filterKeys: ["checkout_block_experiment"],
    test: checkoutTest({ checkoutBlockType: "timer" }),
    personalization: { kind: "checkoutBlock", checkoutBlockType: "timer" },
  },
  {
    id: "text-only",
    title: "Text Only",
    description: "Insert a simple text message block.",
    category: "checkout",
    graphic: "textOnly",
    filterKeys: ["checkout_block_experiment"],
    test: checkoutTest({
      checkoutBlockType: "trustBadge",
      checkoutBlockTemplate: "textOnly",
    }),
    personalization: {
      kind: "checkoutBlock",
      checkoutBlockType: "trustBadge",
      checkoutBlockTemplate: "textOnly",
    },
  },
  {
    id: "post-purchase-upsell",
    title: "Post-purchase Upsell",
    description: "Offer one-click add-ons after checkout.",
    category: "postPurchase",
    graphic: "postPurchaseUpsell",
    filterKeys: ["post_purchase_experiment"],
    test: postPurchaseTest({ postPurchaseType: "post-purchase-upsell" }),
    personalization: {
      kind: "postPurchase",
      postPurchaseType: "post-purchase-upsell",
    },
  },
  {
    id: "post-purchase-funnel",
    title: "Post-purchase Funnel",
    description: "Chain multiple offers after the order.",
    category: "postPurchase",
    graphic: "postPurchaseFunnel",
    filterKeys: ["post_purchase_experiment"],
    badgeContent: "Coming soon!",
    opensRequestAccessForm: true,
  },
];

const emptyFilterKeys: string[] = [];

interface CreateExperimentDialogProps {
  renderLocation: CreateExperimentDialogRenderLocation;
}

export const CreateExperienceDialog = ({
  renderLocation,
}: CreateExperimentDialogProps) => {
  const navigate = useNavigate();
  const dialog = useDialog("createExperiment");
  const { openDialog } = useDialogs();
  const experiencesList = useGetExperiencesList({ category: "experiment" });
  const hasRequiredShopifyScopes = useHasRequiredShopifyScopes([
    "read_checkout_branding_settings",
  ]);
  const hasWriteOrders = useHasRequiredShopifyScopes(["write_orders"]);
  const hasStorefrontToken = useHasStorefrontToken();
  const shopifyPlan = useConfiguration("shopify_plan")?.value;
  const hasPostPurchaseAccess = useConfiguration(
    "enable_post_purchase_checkout"
  )?.value;
  const hasCartMetafields = useConfiguration("enable_cart_metafields")?.value;
  const isUnderOfferLimit = useIsUnderOfferLimit();
  const activeSubscriptions = useActiveSubscription();
  const isCoreRedirectOnly = areOneOfTheseSubsActive(activeSubscriptions, [
    "Core (Redirect Only)",
  ]);
  const isOnCorePlan =
    isCoreRedirectOnly ||
    areOneOfTheseSubsActive(activeSubscriptions, ["Core"]);
  const billingGateModel = useBillingGateModel({ enabled: true });

  const [activeCategory, setActiveCategory] = useState<CategoryFilter>(
    getInitialActiveCategory(renderLocation, undefined)
  );
  const [selectedCard, setSelectedCard] =
    useState<ExperienceCreationCard | null>(null);
  const [gatedModuleId, setGatedModuleId] = useState<string | undefined>();
  const [activeRequestForm, setActiveRequestForm] = useState<{
    url: string;
    title: string;
  }>({
    url: postPurchaseFunnelRequestForm,
    title: "Post-Purchase Funnels Waitlist",
  });

  const filterKeys = dialog.props?.filterKeys ?? emptyFilterKeys;
  const webUiScopeMap = useBooleanWebUiScopeMap();
  const visibleCards = useMemo(() => getVisibleCards(filterKeys), [filterKeys]);
  const availableActiveCategory = getAvailableCategory(
    activeCategory,
    visibleCards
  );
  const gatedDecision = gatedModuleId
    ? billingGateModel.byModuleId[gatedModuleId]
    : undefined;

  const canCreateShippingTest =
    experiencesList.isSuccess &&
    experiencesList.data.experiencesList.filter(
      (exp) => exp.testTypes.hasTestShipping && exp.status !== "ended"
    ).length === 0;

  useEffect(() => {
    if (dialog.isOpen) {
      setSelectedCard(null);
      setActiveCategory(
        getAvailableCategory(
          getInitialActiveCategory(
            renderLocation,
            dialog.props?.showContentSection
          ),
          visibleCards
        )
      );
    }
  }, [
    dialog.isOpen,
    dialog.props?.showContentSection,
    renderLocation,
    visibleCards,
  ]);

  const handleClose = () => {
    dialog.close();
    setSelectedCard(null);
    setGatedModuleId(undefined);
  };

  const isPlusOrPartnerPlan =
    shopifyPlan === "shopify_plus" || shopifyPlan === "partner_test";

  const getRunModeDisabledReason = (
    card: ExperienceCreationCard,
    category: ExperienceCategory
  ) => getRunModeDisabledReasonBase(card, category, { canCreateShippingTest });

  const canCreateOnsiteUpsell = webUiScopeMap["create_onsite_upsell"] === true;

  const getFirstCardDisabledReason = (card: ExperienceCreationCard) =>
    getFirstCardDisabledReasonBase(card, {
      isPlusOrPartnerPlan,
      hasPostPurchaseAccess: !!hasPostPurchaseAccess,
      hasWriteOrders,
      hasStorefrontToken,
      hasCartMetafields: !!hasCartMetafields,
      canCreateOnsiteUpsell,
    });
  const opensBundleOnsiteUpsell = (card: ExperienceCreationCard) =>
    card.id === "bundle-onsite-upsell";
  const opensOnsiteUpsellBeta = (card: ExperienceCreationCard) =>
    isOnsiteUpsellExperience(card) && !canCreateOnsiteUpsell;

  const getOutOfScopeTier = (card: ExperienceCreationCard) =>
    getOutOfScopeTierBase(card, { isCoreRedirectOnly, isOnCorePlan });

  const trackOnsiteUpsellCreationStarted = (card: ExperienceCreationCard) => {
    if (card.id !== "onsite-upsell") return;

    mpOnsiteUpsellCreationStarted({
      upsellType: "simple",
    });
  };

  const trackOfferCreationStarted = (preset: OfferPreset | undefined) => {
    if (!preset) return;

    mpOfferCreationStarted(
      getOfferTypeKey({
        discountApplicationType: preset.discountType,
        discountApplicationMethod: preset.discountMethod,
        unitType: preset.unitType,
      })
    );
  };

  const maybeGateBillingModule = (card: ExperienceCreationCard) => {
    const moduleId = card.test?.moduleId;
    if (!moduleId) return false;

    if (billingGateModel.status !== "ready") return true;

    const decision = billingGateModel.byModuleId[moduleId];
    if (!decision) return true;
    if (decision.kind === "bypass") return false;

    setGatedModuleId(moduleId);
    return true;
  };

  const navigateToTest = (card: ExperienceCreationCard) => {
    if (!card.test) return;
    const disabledReason = getRunModeDisabledReason(card, "experiment");
    if (disabledReason) return;

    if (maybeGateBillingModule(card)) return;

    if (card.test.key === "volume_discount_experiment" && !isUnderOfferLimit) {
      openDialog("offerLimitReached");
      return;
    }

    if (
      card.test.key === "checkout_block_experiment" &&
      !hasRequiredShopifyScopes
    ) {
      openDialog("missingShopifyScope");
      return;
    }

    if (
      card.test.key === "post_purchase_experiment" &&
      !hasPostPurchaseAccess
    ) {
      mpPostPurchaseRequestAccessClick();
      handleClose();
      openDialog("requestFeatureAccessPylon");
      return;
    }

    // TODO - CP what do we do with this
    mpExperienceCreated("test", getDialogSource(renderLocation), [
      card.test.mpProductCategory,
    ]);
    trackOnsiteUpsellCreationStarted(card);
    handleClose();
    void navigate(getTestLink(card.test), {
      state: {
        name: getGeneratedTestName(card),
        ...card.test.state,
      },
    });
  };

  const navigateToPersonalization = (card: ExperienceCreationCard) => {
    const action = card.personalization;
    if (!action) return;

    if (action.kind === "offer" && !isUnderOfferLimit) {
      openDialog("offerLimitReached");
      return;
    }

    if (action.kind === "checkoutBlock" && !hasRequiredShopifyScopes) {
      openDialog("missingShopifyScope");
      return;
    }

    if (action.kind === "postPurchase" && !hasPostPurchaseAccess) {
      mpPostPurchaseRequestAccessClick();
      handleClose();
      openDialog("requestFeatureAccessPylon");
      return;
    }

    mpExperienceCreated("personalization", getDialogSource(renderLocation), [
      card.test?.mpProductCategory ?? "personalization",
    ]);
    trackOnsiteUpsellCreationStarted(card);
    trackOfferCreationStarted(
      action.kind === "offer" ? action.preset : undefined
    );
    handleClose();

    if (action.kind === "mod") {
      void navigate(PERSONALIZATION_ROUTES.Personalization, {
        state: {
          source: "scratchCards",
          modToActivate: action.modToActivate,
        },
      });
      return;
    }

    if (action.kind === "offer") {
      void navigate(PERSONALIZATION_ROUTES.Personalization, {
        state: {
          source: "scratchCards",
          showOfferDialog: true,
          discountType: action.preset.discountType,
          discountMethod: action.preset.discountMethod,
          unitType: action.preset.unitType,
          isCreate: true,
          isEdit: false,
        },
      });
      return;
    }

    if (action.kind === "checkoutBlock") {
      void navigate(PERSONALIZATION_ROUTES.Personalization, {
        state: {
          source: "scratchCards",
          showCheckoutBlocksDialog: true,
          checkoutBlockType: action.checkoutBlockType,
          checkoutBlockTemplate: action.checkoutBlockTemplate,
          isCreate: true,
          isEdit: false,
          isUpsell: action.checkoutBlockType === "upsell",
        },
      });
      return;
    }

    void navigate(PERSONALIZATION_ROUTES.Personalization, {
      state: {
        source: "scratchCards",
        showPostPurchaseDialog: true,
        postPurchaseType: action.postPurchaseType,
        isCreate: true,
        isEdit: false,
      },
    });
  };

  const handleRunMode = (
    card: ExperienceCreationCard,
    category: ExperienceCategory
  ) => {
    if (category === "experiment") {
      navigateToTest(card);
    } else {
      navigateToPersonalization(card);
    }
  };

  const handleCardSelect = (card: ExperienceCreationCard) => {
    if (getFirstCardDisabledReason(card)) return;

    if (opensBundleOnsiteUpsell(card)) {
      setActiveRequestForm({
        url: bundleOnsiteUpsellRequestForm,
        title: "Bundle Onsite Upsells",
      });
      handleClose();
      openDialog("requestFeatureAccessPylon");
      return;
    }

    if (opensOnsiteUpsellBeta(card)) {
      setActiveRequestForm({
        url: onsiteUpsellBetaRequestForm,
        title: "Onsite Upsells Beta",
      });
      handleClose();
      openDialog("requestFeatureAccessPylon");
      return;
    }

    if (card.opensRequestAccessForm) {
      setActiveRequestForm({
        url: postPurchaseFunnelRequestForm,
        title: "Post-Purchase Funnels Waitlist",
      });
      mpPostPurchaseFunnelsRequestAccessClick();
      handleClose();
      openDialog("requestFeatureAccessPylon");
      return;
    }

    const canRunTest = !getRunModeDisabledReason(card, "experiment");
    const canCreatePersonalization = !getRunModeDisabledReason(
      card,
      "personalization"
    );

    if (canRunTest && !canCreatePersonalization) {
      navigateToTest(card);
      return;
    }

    if (!canRunTest && canCreatePersonalization) {
      navigateToPersonalization(card);
      return;
    }

    setSelectedCard(card);
  };

  return (
    <>
      <RequestFeatureAccessPylonDialog
        requestFormUrl={activeRequestForm.url}
        title={activeRequestForm.title}
      />
      <MissingShopifyScopeDialog />
      {gatedModuleId && gatedDecision?.kind === "gate" && (
        <PricingGatedModal
          decision={gatedDecision}
          moduleId={gatedModuleId}
          open={true}
          onClose={() => setGatedModuleId(undefined)}
          onContinueToPayment={() => {
            const moduleId = gatedModuleId;
            setGatedModuleId(undefined);
            dialog.close();
            if (
              moduleId === SMART_PRICING_LIMITED_ID ||
              moduleId === SMART_PRICING_UNLIMITED_ID
            ) {
              mpSmartPricingUpgradeStarted({
                orderVolumeTier: gatedDecision.tier,
                tierSelected: moduleId,
                userState: gatedDecision.userState,
              });
            } else {
              mpGatedModuleAddStarted({
                moduleName: moduleId,
                source: "modal",
                userState: gatedDecision.userState,
              });
            }
            void navigate(
              `${SETTINGS_ROUTES.Settings}?selected=${moduleId}#${SettingsSectionId.Billing}`
            );
          }}
        />
      )}
      {experiencesList.isSuccess && dialog.isOpen && (
        <IgDialog
          dontUseDialogContent={true}
          minWidth="850px"
          maxWidth="md"
          hideActions={true}
          dialogHeight={null}
          padding="0"
          handleClose={handleClose}
          title=""
          showDialog={dialog.isOpen}
          rawContent={
            selectedCard ? (
              <CategorySelectionPage
                card={selectedCard}
                getRunModeDisabledReason={getRunModeDisabledReason}
                onBack={() => setSelectedCard(null)}
                onClose={handleClose}
                onRunMode={handleRunMode}
              />
            ) : (
              <TypeSelectionPage
                activeCategory={availableActiveCategory}
                visibleCards={visibleCards}
                getDisabledReason={getFirstCardDisabledReason}
                getOutOfScopeTier={getOutOfScopeTier}
                onCategoryChange={setActiveCategory}
                onClose={handleClose}
                onSelect={handleCardSelect}
              />
            )
          }
        />
      )}
    </>
  );
};
